import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Navigate } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { IUserTeamRoles } from 'app/library/team/interfaces/user-team-roles.interface';
import { UserTeamRoles } from 'app/library/team/models/user-team-roles';
import { TeamService } from 'app/library/team/services/team.service';
import { IEntityNode } from 'app/projects/shared/src/lib/interfaces/entity-node.interface';
import { SimpleEntity } from 'app/projects/shared/src/lib/models/simple-entity.model';

@Component({
    selector: 'app-select-teams-dialog',
    templateUrl: './select-teams-dialog.component.html',
    styleUrls: ['./select-teams-dialog.component.scss'],
})
export class SelectTeamsDialogComponent {
    @Input()
    listTree: IEntityNode[];

    @Input()
    title: string;

    @Input()
    alreadySelectedTeamIds: string[] = [];

    @Input()
    performanceTemplateTypes: SimpleEntity[];

    @Input()
    showSelectRole: boolean = false;

    selectedRole = new FormControl();
    userTeamRoles = UserTeamRoles;

    get filteredUserTeamRoles(): IUserTeamRoles[] {
        return this.userTeamRoles.all.filter((role) => role.name !== 'team_leader_sub_teams');
    }

    treeControl = new NestedTreeControl<IEntityNode>((node) => node.children);
    dataSource = new MatTreeNestedDataSource<IEntityNode>();

    selectPerformanceTemplateTeams: boolean = false;
    selectedTeams: IEntityNode[] = [];

    constructor(
        private _store: Store,
        private _teamService: TeamService,
        public dialogRef: MatDialogRef<SelectTeamsDialogComponent>
    ) {}

    initDialog(): void {
        this.selectedTeams = [];
        this.resetSelectionState(this.listTree);
        this.dataSource.data = this.listTree;
        this.markAlreadySelectedTeams();
    }

    isNodeSelected(node: IEntityNode): boolean {
        return this.selectedTeams.some((team) => team.id === node.id);
    }

    hasChild = (_: number, node: IEntityNode) =>
        (!!node.children && node.children.length > 0) || node.childrenCount > 0;

    async toggleNode(node: IEntityNode): Promise<void> {
        if (this.treeControl.isExpanded(node)) {
            this.treeControl.collapse(node);
            return;
        }

        if (node.childrenCount > 0 && (!node.children || node.children.length === 0)) {
            node.isLoading = true;
            this.treeControl.expand(node);

            const children = await this._teamService.fetchParentChildren(node.id);
            if (children) {
                node.children = children.map((child) => this.createTeamNode(child, node.id));
                this.markLoadedChildren(node.children, node);
            }

            node.isLoading = false;
            this.refreshDataSource();
            this.treeControl.expand(node);
        } else {
            this.treeControl.expand(node);
        }
    }

    onSelect(selectedTeam: IEntityNode): void {
        selectedTeam.isSelected = true;
        this.selectedTeams.push(selectedTeam);

        if (!this.showSelectRole && selectedTeam.children && selectedTeam.children.length > 0) {
            this.selectChildrenRecursively(selectedTeam.children);
        }
    }

    onManageTeams(): void {
        this.dialogRef.close();
        this._store.dispatch(new Navigate([`/admin/people-development/organization-review/templates`]));
    }

    selectChildrenRecursively(children: IEntityNode[]): void {
        children.forEach((child) => {
            child.isParentSelected = true;
            this.onDeselect(child);

            if (child.children && child.children.length > 0) {
                this.selectChildrenRecursively(child.children);
            }
        });
    }

    onDeselect(selectedTeam: IEntityNode): void {
        selectedTeam.isSelected = false;
        const index = this.selectedTeams.indexOf(selectedTeam);
        if (index > -1) {
            this.selectedTeams.splice(index, 1);
        }

        if (!this.showSelectRole && selectedTeam.children && selectedTeam.children.length > 0) {
            this.deselectChildrenRecursively(selectedTeam.children);
        }
    }

    deselectChildrenRecursively(children: IEntityNode[]): void {
        children.forEach((child) => {
            child.isParentSelected = false;

            if (child.children && child.children.length > 0) {
                this.deselectChildrenRecursively(child.children);
            }
        });
    }

    markAlreadySelectedTeams(): void {
        if (!this.listTree?.length) {
            return;
        }

        const selectTeamsRecursively = (nodes: IEntityNode[]): void => {
            nodes.forEach((node) => {
                if (this.alreadySelectedTeamIds.includes(node.id)) {
                    this.onSelect(node);
                }

                this.applyPerformanceTemplateSelectability(node);

                if (node.children?.length) {
                    selectTeamsRecursively(node.children);
                }
            });
        };

        selectTeamsRecursively(this.listTree);
    }

    onSave(): void {
        const result = { selectedTeams: this.selectedTeams };

        if (this.showSelectRole) {
            result['selectedRole'] = this.selectedRole.value;
        }

        this.dialogRef.close(result);
    }

    private createTeamNode(team: { id: string; name: string; subteamCount: number; parent?: { id: string } }, parentId?: string): IEntityNode {
        return {
            id: team.id,
            name: team.name,
            children: [],
            childrenCount: team.subteamCount || 0,
            parent: parentId ? { id: parentId } : team.parent,
            isSelected: false,
            isParentSelected: false,
        };
    }

    private resetSelectionState(nodes: IEntityNode[]): void {
        nodes?.forEach((node) => {
            node.isSelected = false;
            node.isParentSelected = false;

            if (node.children?.length) {
                this.resetSelectionState(node.children);
            }
        });
    }

    private markLoadedChildren(children: IEntityNode[], parentNode: IEntityNode): void {
        // Cover both direct parent selection and ancestor-inherited selection
        // (e.g. MaxBrain selected → Customer Success → Devs).
        const isCoveredByParentSelection = this.isNodeSelected(parentNode) || !!parentNode.isParentSelected;

        children.forEach((child) => {
            child.isSelected = false;
            child.isParentSelected = isCoveredByParentSelection;
            this.applyPerformanceTemplateSelectability(child);

            // Parent selection already covers children in the UI; only restore
            // independent selection when the parent is not selected.
            if (!isCoveredByParentSelection && this.alreadySelectedTeamIds.includes(child.id)) {
                this.onSelect(child);
            }
        });
    }

    private applyPerformanceTemplateSelectability(node: IEntityNode): void {
        if (!this.selectPerformanceTemplateTeams) {
            return;
        }

        node.isSelectable = this.performanceTemplateTypes?.some((templateType) => templateType.id === node.id) ?? false;
    }

    private refreshDataSource(): void {
        const data = this.dataSource.data;
        this.dataSource.data = null;
        this.dataSource.data = data;
    }
}
